import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Star, Users, Send, ThumbsUp, User } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StarRating({ value, onChange, size = 'w-6 h-6' }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(s => (
        <button key={s} onClick={() => onChange(s)} type="button"
          className="transition-transform hover:scale-110">
          <Star className={`${size} ${s <= value ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'}`} />
        </button>
      ))}
    </div>
  );
}

export default function ClientRateCaregiver() {
  const [caregivers, setCaregivers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [myRatings, setMyRatings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/client-portal/my-caregivers`, { withCredentials: true }),
      axios.get(`${API_URL}/api/client-portal/my-caregiver-ratings`, { withCredentials: true }),
    ]).then(([cgRes, ratRes]) => {
      setCaregivers(cgRes.data);
      setMyRatings(ratRes.data);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async () => {
    if (!selected || rating === 0) { toast.error('Select a caregiver and rating'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/client-portal/rate-caregiver`, {
        caregiver_id: selected.id, rating, review
      }, { withCredentials: true });
      toast.success('Rating submitted');
      setRating(0); setReview(''); setSelected(null);
      const { data } = await axios.get(`${API_URL}/api/client-portal/my-caregiver-ratings`, { withCredentials: true });
      setMyRatings(data);
    } catch (err) {
      toast.error('Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="client-rate-caregiver-page">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">Rate My Caregivers</h1>
          <p className="text-sm text-muted-foreground mt-1">Leave feedback for your care team</p>
        </div>

        {/* Select Caregiver */}
        <Card className="glass-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <Users className="w-5 h-5 text-[#00A6A0]" /> Select Caregiver
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {caregivers.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-4">No caregivers assigned yet</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {caregivers.map(cg => (
                  <button key={cg.id}
                    onClick={() => { setSelected(cg); setRating(0); setReview(''); }}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      selected?.id === cg.id
                        ? 'border-[#00A6A0] bg-[#00A6A0]/5'
                        : 'border-border hover:bg-muted/30'
                    }`}
                    data-testid={`select-cg-${cg.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#00A6A0]/10 flex items-center justify-center overflow-hidden">
                        {cg.profile_photo_url ? (
                          <img src={cg.profile_photo_url} className="w-full h-full object-cover" alt="" />
                        ) : (
                          <User className="w-5 h-5 text-[#00A6A0]" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{cg.name}</p>
                        {cg.avg_rating && (
                          <div className="flex items-center gap-1">
                            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                            <span className="text-xs text-muted-foreground">{cg.avg_rating} ({cg.rating_count})</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rating Form */}
        {selected && (
          <Card className="glass-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-lg font-heading text-foreground">
                Rate {selected.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="text-center">
                <StarRating value={rating} onChange={setRating} size="w-10 h-10" />
                <p className="text-sm text-muted-foreground mt-2">
                  {rating === 0 ? 'Tap to rate' : `${rating} out of 5`}
                </p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Comments (optional)</label>
                <Textarea value={review} rows={3}
                  onChange={e => setReview(e.target.value)}
                  placeholder="Share your experience..."
                  className="bg-muted border-border" data-testid="cg-review-text" />
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSubmit} disabled={submitting} className="btn-primary gap-2" data-testid="submit-cg-rating-btn">
                  <Send className="w-4 h-4" /> {submitting ? 'Submitting...' : 'Submit Rating'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Past Ratings */}
        {myRatings.length > 0 && (
          <Card className="glass-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
                <ThumbsUp className="w-5 h-5 text-amber-400" /> My Past Ratings
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {myRatings.map((r, i) => (
                  <div key={i} className="p-4 flex items-center justify-between" data-testid={`past-rating-${i}`}>
                    <div>
                      <p className="text-sm text-foreground font-medium">Caregiver</p>
                      {r.review && <p className="text-xs text-muted-foreground mt-0.5">{r.review}</p>}
                      <p className="text-xs text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      <span className="text-sm font-bold text-foreground">{r.rating}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
